import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ThemePreference,
  UserPreferences,
} from "@chat-yai/shared/admin-types";
import { preferencesApi } from "@/api/endpoints/preferences";
import { ApiError } from "@/api/client";
import { useSession } from "@/contexts/SessionContext";

const STORAGE_KEY = "chat-yai-admin:prefs";

interface PreferencesContextValue {
  prefs: UserPreferences;
  // Effective theme after resolving "auto" against the OS color scheme.
  resolvedTheme: "light" | "dark";
  setTheme: (theme: ThemePreference) => void;
  isUpdating: boolean;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

const DEFAULT_PREFS: UserPreferences = {
  theme: "light",
  density: "comfortable",
  extras: {},
};

function readLocal(): UserPreferences | null {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    return {
      theme: parsed.theme === "dark" || parsed.theme === "auto" ? parsed.theme : "light",
      density: parsed.density === "compact" ? "compact" : "comfortable",
      extras: parsed.extras ?? {},
    };
  } catch {
    return null;
  }
}

function writeLocal(p: UserPreferences): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    }
  } catch {
    // ignore quota / disabled storage
  }
}

function applyToDom(theme: "light" | "dark"): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
}

function detectSystem(): "light" | "dark" {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function PreferencesProvider({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated } = useSession();
  const queryClient = useQueryClient();

  const [prefs, setPrefs] = useState<UserPreferences>(() => readLocal() ?? DEFAULT_PREFS);
  const [system, setSystem] = useState<"light" | "dark">(() => detectSystem());

  // Listen for OS theme changes so "auto" stays accurate without a reload.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (): void => setSystem(mq.matches ? "dark" : "light");
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    // Safari < 14 fallback.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  // Pull authoritative copy when the user signs in. We don't gate the UI on
  // this query — local cache renders instantly and we reconcile when remote
  // arrives.
  const remoteQuery = useQuery({
    queryKey: ["me", "preferences"],
    queryFn: () => preferencesApi.get(),
    enabled: isAuthenticated,
    retry: (count, err) => (err instanceof ApiError && err.status === 401 ? false : count < 1),
    staleTime: 60_000,
  });

  // Merge remote → local once it lands.
  const lastRemoteApplied = useRef<UserPreferences | null>(null);
  useEffect(() => {
    if (remoteQuery.data && remoteQuery.data !== lastRemoteApplied.current) {
      lastRemoteApplied.current = remoteQuery.data;
      setPrefs(remoteQuery.data);
      writeLocal(remoteQuery.data);
    }
  }, [remoteQuery.data]);

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<UserPreferences>) =>
      preferencesApi.update({
        ...(patch.theme ? { theme: patch.theme } : {}),
        ...(patch.density ? { density: patch.density } : {}),
        ...(patch.extras ? { extras: patch.extras } : {}),
      }),
    onSuccess: (next) => {
      lastRemoteApplied.current = next;
      setPrefs(next);
      writeLocal(next);
      queryClient.setQueryData(["me", "preferences"], next);
    },
  });

  const resolvedTheme: "light" | "dark" = useMemo(() => {
    if (prefs.theme === "dark") return "dark";
    if (prefs.theme === "light") return "light";
    return system;
  }, [prefs.theme, system]);

  // Apply theme to <html> as soon as it changes — this includes the initial
  // local-storage hydration so first paint matches user choice (no flash).
  useEffect(() => {
    applyToDom(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback(
    (theme: ThemePreference) => {
      // Optimistic local update. Persist to backend if signed in; otherwise
      // localStorage carries the choice across sessions.
      const next: UserPreferences = { ...prefs, theme };
      setPrefs(next);
      writeLocal(next);
      if (isAuthenticated) {
        updateMutation.mutate({ theme });
      }
    },
    [prefs, isAuthenticated, updateMutation],
  );

  const value = useMemo<PreferencesContextValue>(
    () => ({ prefs, resolvedTheme, setTheme, isUpdating: updateMutation.isPending }),
    [prefs, resolvedTheme, setTheme, updateMutation.isPending],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences must be used inside PreferencesProvider");
  return ctx;
}
